import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconDirectionLeft } from '@components/icons/direction-left';
import { IconDirectionRight } from '@components/icons/direction-right';
import { theme } from '@styles/theme';
import { transitionStandard } from '@utils/transitions';
import { XOR } from '@utils/types';

import { JumpToPage } from './jump-to-page';

import { Stack } from '../stack';

type SimplePaginationProps = {
  count: number;
  selected: number;
  onSelect(index: number): void;
  justify?: 'left' | 'center' | 'right';
  alwaysShow?: boolean;
};

export type PaginationProps = Omit<SimplePaginationProps, 'count'> &
  XOR<{ count: number }, { pageSize?: number; itemCount: number }>;

export function Pagination(props: PaginationProps) {
  const {
    selected,
    count: userCount,
    onSelect,
    justify = 'left',
    alwaysShow = false,
    pageSize = 25,
    itemCount,
  } = props;
  const count = userCount ?? Math.ceil(itemCount / pageSize);

  if (count < 2 && !alwaysShow) return null;
  if (count < 6) return <PaginationSimple {...props} count={count} />;

  const onSelectOnce = (index: number) => {
    if (selected === index) return;
    onSelect(index);
  };

  return (
    <Stack
      justify={justifyMap[justify]}
      orientation="horizontal"
      space={2}
      as="nav"
      role="navigation"
      aria-label="Pagination"
    >
      <IconButton
        disabled={selected < 2}
        onClick={() => onSelectOnce(selected - 1)}
        icon={<IconDirectionLeft />}
      />
      <Page onClick={() => onSelectOnce(1)} {...ariaPropsForIndex(1, selected)}>
        1
      </Page>

      {selected <= 3 && (
        <>
          <Page onClick={() => onSelectOnce(2)} {...ariaPropsForIndex(2, selected)}>
            2
          </Page>
          <Page onClick={() => onSelectOnce(3)} {...ariaPropsForIndex(3, selected)}>
            3
          </Page>
          <JumpToPage from={4} to={count - 1} onSelect={onSelectOnce} />
        </>
      )}

      {selected > 3 && selected < count - 2 && (
        <>
          <JumpToPage from={2} to={selected - 1} onSelect={onSelectOnce} />
          <Page {...ariaPropsForIndex(selected, selected)}>{selected}</Page>
          <JumpToPage from={selected + 1} to={count - 1} onSelect={onSelectOnce} />
        </>
      )}

      {selected > count - 3 && (
        <>
          <JumpToPage from={2} to={count - 3} onSelect={onSelectOnce} />
          <Page onClick={() => onSelectOnce(count - 2)} {...ariaPropsForIndex(count - 2, selected)}>
            {count - 2}
          </Page>
          <Page onClick={() => onSelectOnce(count - 1)} {...ariaPropsForIndex(count - 1, selected)}>
            {count - 1}
          </Page>
        </>
      )}

      <Page onClick={() => onSelectOnce(count)} {...ariaPropsForIndex(count, selected)}>
        {count}
      </Page>

      <IconButton
        disabled={selected >= count}
        onClick={() => onSelectOnce(selected + 1)}
        icon={<IconDirectionRight />}
      />
    </Stack>
  );
}

function PaginationSimple({ selected, onSelect, count, justify = 'left' }: SimplePaginationProps) {
  const pages = Array.from({ length: count }).map((_, i) => {
    return (
      <Page key={i} onClick={() => onSelect(i + 1)} {...ariaPropsForIndex(i + 1, selected)}>
        {i + 1}
      </Page>
    );
  });

  return (
    <Stack
      justify={justifyMap[justify]}
      orientation="horizontal"
      space={2}
      as="nav"
      role="navigation"
      aria-label="Pagination"
    >
      <IconButton
        disabled={selected < 2}
        onClick={() => onSelect(selected - 1)}
        icon={<IconDirectionLeft />}
      />
      {pages}
      <IconButton
        disabled={selected >= count}
        onClick={() => onSelect(selected + 1)}
        icon={<IconDirectionRight />}
      />
    </Stack>
  );
}

function ariaPropsForIndex(index: number, selectedIndex: number) {
  return {
    'aria-label': selectedIndex === index ? `Current page, page ${index}` : `Goto page ${index}`,
    ...(selectedIndex === index && { 'aria-current': true }),
  };
}

const hoveredStyle = {
  backgroundColor: theme.color.uiTrans10,
  svg: {
    color: theme.color.buttonIconPositiveHover,
  },
};

const BaseButton = styled.button({
  height: theme.base.height,
  width: theme.base.height,
  transition: transitionStandard('all'),
  display: 'flex',
  outline: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  font: theme.font.semibold14,
  backgroundColor: theme.color.uiTrans5,
  border: 'none',
  borderRadius: '0.25rem',
  '--shadow-focus-gap': '0px',
  '--shadow-focus-width': '0.15rem',

  '&:not(:disabled)': {
    '&:focus-visible': {
      boxShadow: theme.shadow.focus,
      '--shadow-focus-color': theme.color.purple50,
    },
    '&:not([aria-current="true"])': {
      cursor: 'pointer',

      '&:hover': hoveredStyle,

      '&:active': {
        transform: 'scale(0.97)',
        ...hoveredStyle,
      },
    },
  },
  '&[aria-current="true"]': {
    cursor: 'default',
    '--shadow-focus-color': theme.color.purple100,
  },
});

const Page = styled(BaseButton)({
  color: theme.color.textSecondary,

  '&[aria-current="true"]': {
    color: theme.color.white100,
    backgroundColor: theme.color.buttonPrimary,

    '&:active': {
      transform: 'none',
    },
  },
});

const justifyMap = {
  center: 'center',
  left: 'flex-start',
  right: 'flex-end',
} as const;
